import Card from '$tablet-app/src/app/components/Card';
import { useFloatingActionControl } from '$tablet-app/src/app/components/FloatingActions';
import { ScreenBackButton } from '$tablet-app/src/app/components/ScreenBackButton';
import { ScreenHeader } from '$tablet-app/src/app/components/ScreenHeader';
import { ScreenTitle } from '$tablet-app/src/app/components/ScreenTitle';
import Spacer from '$tablet-app/src/app/components/Spacer';
import TextInputMultilineGrow from '$tablet-app/src/app/components/TextInputMultilineGrow';
import { Button } from '@akin-home/buttons/native';
import {
  ContactModel,
  findFirstEmailAddress,
  findFirstPhoneNumber,
  PhoneNumber,
} from '@akin-home/contacts';
import { TextInput } from '@akin-home/forms/native';
import { Text } from '@akin-home/text/native';
import { colors, spacing } from '@akin-home/theme';
import { showErrorToast } from '@akin-home/toast/native';
import React, { useEffect } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import {
  useCreateVideoCallView,
  VideoCallInvitationMode,
} from '../../hooks/useCreateVideoCallView';

export interface CreateVideoCallScreenOnSend {
  mode: 'email' | 'sms';
  email?: string;
  phone_number?: string;
  invitation_body?: string;
  save_email_to_contact?: boolean;
  save_phone_number_to_contact?: boolean;
}

interface Props {
  /** The contact having their invitation sent to  */
  contact?: ContactModel;

  onCancel?(): void;

  onSend?(data: CreateVideoCallScreenOnSend): Promise<{
    success?: boolean;
    error?: { code: string; message: string };
  }>;
}

const SMS_MESSAGE_CHAR_LIMIT = 90;

/**
 * This screen allows the user to do the following
 *
 * - Select email or phone number to send video call invitation to
 * - Add an email or phone number for contact if one doesn't exist
 * - Modify video call invitation
 * - Confirm send invitation
 *
 * Upon confirmation, user is directed to video call screen
 */
export const CreateVideoCallScreen = (props: Props) => {
  const { contact, onCancel, onSend } = props;

  const { scrollHandler } = useFloatingActionControl();

  const {
    state,
    sending_disabled,
    setContact,
    setInvitationBody,
    setSendingInvitation,
    setError,
    selectEmail,
    setNewEmail,
    setInvitationMode,
    selectPhoneNumber,
    setNewPhoneNumber,
  } = useCreateVideoCallView();

  const no_email = !findFirstEmailAddress(state.contact);
  const no_phone = !findFirstPhoneNumber(state.contact);

  useEffect(() => {
    if (state.error?.message) {
      showErrorToast({
        text1: 'Submission Error',
        text2: state.error.message,
      });
    }
  }, [state.error]);

  const onPressSend = async () => {
    if (!onSend) return;

    if (
      state.invitation_mode === 'email' &&
      !state.selected_email?.length &&
      !state.new_email?.length
    ) {
      setError({
        code: 'no_email_specified',
        message: 'No Email Specified',
      });
      return;
    }

    if (
      state.invitation_mode === 'sms' &&
      !state.selected_phone_number?.length &&
      !state.new_phone_number?.length
    ) {
      setError({
        code: 'no_phone_number_specified',
        message: 'No phone number specified',
      });
      return;
    }

    if (
      state.invitation_mode === 'sms' &&
      (state.invitation_body?.length ?? 0) > SMS_MESSAGE_CHAR_LIMIT
    ) {
      setError({
        code: 'sms_message_above_char_limit',
        message: 'Message is longer than the character limit',
      });
      return;
    }

    setSendingInvitation();

    const use_new_email = !!state.new_email?.length;
    const use_new_phone = !!state.new_phone_number?.length;

    const res = await onSend({
      mode: state.invitation_mode,
      email: use_new_email ? state.new_email : state.selected_email,
      phone_number: use_new_phone
        ? state.new_phone_number
        : state.selected_phone_number,
      invitation_body: state.invitation_body,
      save_email_to_contact: use_new_email,
      save_phone_number_to_contact: use_new_phone,
    });

    if (res.error) {
      setError(res.error);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onChangeMode = (mode: VideoCallInvitationMode) => () =>
    setInvitationMode(mode);

  useEffect(() => {
    if (contact && !state.contact) {
      setContact(contact);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact, state.contact]);

  const is_body_over_limit =
    state.invitation_mode === 'sms' &&
    (state.invitation_body?.length ?? 0) > SMS_MESSAGE_CHAR_LIMIT;

  return (
    <View style={styles.screen}>
      <ScreenHeader style={styles.screenHeader}>
        <ScreenBackButton onPress={onCancel} />

        <ScreenTitle>{'Create Video Call'}</ScreenTitle>
      </ScreenHeader>

      <Animated.ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContainer}
        onScroll={scrollHandler}
      >
        <Card style={styles.card}>
          <View style={styles.contentContainer}>
            <View style={styles.contentSegment}>
              <Text weight="bold">
                Send video call invitation to {state.contact?.name}
              </Text>

              <Text>
                They will be sent an email with a link to join the video call.
              </Text>
            </View>

            {/* 
            // Due to business requirements not supporting SMS, this has been commented out.
            <View style={styles.contentSegment}>
              <Text weight="semibold">Send By:</Text>
              <Checkbox
                label="Email"
                value={state.invitation_mode === 'email'}
                onChange={onChangeMode('email')}
              />

              <Checkbox
                label="SMS"
                value={state.invitation_mode === 'sms'}
                onChange={onChangeMode('sms')}
              />
            </View> */}

            {state.invitation_mode === 'email' ? (
              no_email ? (
                <View style={styles.contentSegment}>
                  <Text weight="semibold">
                    This contact has no email, enter one now
                  </Text>

                  <TextInput
                    testID="new-email-input"
                    placeholder="Email Address"
                    value={state.new_email || ''}
                    onChangeText={setNewEmail}
                  />
                </View>
              ) : (
                <View>
                  {state.contact?.emailAddresses.map((email_address, idx) => (
                    <EmailRow
                      key={email_address + idx}
                      email={email_address}
                      selected={state.selected_email === email_address}
                      selectEmail={selectEmail}
                    />
                  ))}
                </View>
              )
            ) : no_phone ? (
              <View>
                <Text weight="semibold">
                  This contact has no mobile number, enter one now
                </Text>
                <Spacer height="8px" />
                <TextInput
                  testID="new-phone-number-input"
                  placeholder="Mobile Number"
                  value={state.new_phone_number || ''}
                  onChangeText={setNewPhoneNumber}
                />
              </View>
            ) : (
              <View>
                {state.contact?.phoneNumbers.map((phone_number, idx) => (
                  <PhoneNumberRow
                    key={phone_number.number + idx}
                    phone_number={phone_number}
                    selected={
                      state.selected_phone_number === phone_number.number
                    }
                    selectPhoneNumber={selectPhoneNumber}
                  />
                ))}
              </View>
            )}

            <View style={styles.contentSegment}>
              <View style={styles.messageHeader}>
                <Text weight="semibold">Message (optional)</Text>
                {state.invitation_mode === 'sms' ? (
                  <Text
                    style={[
                      styles.messageCharCounter,
                      is_body_over_limit && styles.messageCharCounterError,
                    ]}
                    variant="p2"
                    weight={is_body_over_limit ? 'semibold' : 'regular'}
                  >
                    {`Characters: ${state.invitation_body?.length ?? 0}/${90}`}
                  </Text>
                ) : null}
              </View>

              <TextInputMultilineGrow
                placeholder="Add your own message"
                testID="invitation-body-input"
                value={state.invitation_body || ''}
                onChangeText={setInvitationBody}
                style={styles.messageTextInput}
                textAlignVertical="top"
              />
            </View>

            <View style={styles.buttonRow}>
              <Button
                style={styles.button}
                size="large"
                variant="secondary"
                onPress={onCancel}
              >
                Cancel
              </Button>

              <Button
                style={styles.button}
                size="large"
                variant={sending_disabled ? 'secondary' : undefined}
                disabled={sending_disabled}
                onPress={onPressSend}
              >
                {state.status === 'sending_invitation' ? 'Sending...' : 'Send'}
              </Button>
            </View>
          </View>
        </Card>
      </Animated.ScrollView>
    </View>
  );
};

const EmailRow = (props: {
  email: string;
  selected?: boolean;
  selectEmail?(number: string): void;
}) => {
  const { email, selectEmail, selected } = props;
  const onPress = () => selectEmail?.(email);

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        { opacity: pressed ? 0.5 : 1 },
        styles.sendByButton,
        selected ? styles.sendByButtonSelected : {},
      ]}
    >
      <Text color={selected ? 'white' : undefined}>{email}</Text>
    </Pressable>
  );
};

const PhoneNumberRow = (props: {
  phone_number: PhoneNumber;
  selected?: boolean;
  selectPhoneNumber?(number: string): void;
}) => {
  const { phone_number, selectPhoneNumber, selected } = props;
  const onPress = () => selectPhoneNumber?.(phone_number.number);

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        { opacity: pressed ? 0.5 : 1 },
        styles.sendByButton,
        selected ? styles.sendByButtonSelected : {},
      ]}
    >
      <Text color={selected ? 'white' : undefined}>{phone_number.number}</Text>
      <Text color={selected ? 'white' : undefined} variant="caption">
        {phone_number.type}
      </Text>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
  },
  screenHeader: {
    gap: 20,
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  card: {
    padding: spacing[4],
  },
  scroll: {
    flex: 1,
  },
  scrollContainer: {
    paddingHorizontal: spacing[5],
    paddingBottom: spacing[1],
    alignSelf: 'center',
    minWidth: 1000,
    maxWidth: '100%',
  },
  contentContainer: {
    gap: spacing[4],
  },
  contentSegment: {
    gap: spacing[1],
  },
  messageHeader: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  messageCharCounter: {
    marginLeft: 'auto',
    marginRight: spacing[2],
  },
  messageCharCounterError: {
    color: colors.error[60],
  },
  messageTextInput: {
    minHeight: 180,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 16,
  },
  button: {
    flex: 0,
    minWidth: '30%',
  },
  sendByButton: {
    padding: spacing[4],
    borderWidth: 1,
    borderRadius: 10,
    margin: spacing[1],
  },
  sendByButtonSelected: {
    backgroundColor: colors.primary[700],
    borderColor: colors.primary[700],
    color: 'white',
  },
});
